import { chooseSpatialTarget, isGamepadFocusable, type Direction } from './gamepad';

export type CombatSide = 'heroes' | 'foes';
export type CombatAim = 'allies' | 'opposite';
export type CombatFollow = 'actions' | 'target' | 'home' | 'actor' | 'idle' | 'burn';

export type CombatStep = {
  click?: HTMLElement | null;
  follow?: CombatFollow;
  aim?: CombatAim;
  burnKey?: string | null;
  burnRole?: string | null;
};

function rectOf(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

export function isCombatAiming(root: ParentNode): boolean {
  return root.querySelector('[data-gamepad-aiming="true"]') != null;
}

export function combatCards(root: ParentNode, side?: CombatSide): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-combat-card]'))
    .filter((card) => isGamepadFocusable(card))
    .filter((card) => !side || card.getAttribute('data-gamepad-side') === side);
}

export function combatActions(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-combat-action]'))
    .filter((action) => isGamepadFocusable(action));
}

export function combatBurns(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-combat-burn]'))
    .filter((action) => isGamepadFocusable(action));
}

function combatActionPeers(root: ParentNode): HTMLElement[] {
  return [...combatBurns(root), ...combatActions(root)];
}

/** Nach Auslösen denselben Knopf, nach Löschen den nächsten Brennen-Knopf oder die Aktionsleiste. */
export function focusAfterBurn(root: ParentNode, key: string | null, role: string | null): HTMLElement | null {
  const burns = combatBurns(root);
  if (key && role) {
    const same = burns.find((entry) => (
      entry.getAttribute('data-gamepad-burn-key') === key
      && entry.getAttribute('data-gamepad-burn') === role
    ));
    if (same) return same;
  }
  if (key) {
    const sibling = burns.find((entry) => entry.getAttribute('data-gamepad-burn-key') === key);
    if (sibling) return sibling;
  }
  return burns[0] ?? combatActions(root)[0] ?? null;
}

export function combatActorCard(root: ParentNode): HTMLElement | null {
  const actor = root.querySelector<HTMLElement>('[data-gamepad-combat-card][aria-pressed="true"]');
  return actor && isGamepadFocusable(actor) ? actor : null;
}

function firstCard(root: ParentNode, side: CombatSide): HTMLElement | null {
  return combatCards(root, side)[0] ?? null;
}

export function combatHome(root: ParentNode): HTMLElement | null {
  const actor = combatActorCard(root);
  if (actor?.getAttribute('data-gamepad-side') === 'heroes') return actor;
  return firstCard(root, 'heroes') ?? actor;
}

export function combatActorFocus(root: ParentNode): HTMLElement | null {
  return combatActorCard(root) ?? firstCard(root, 'heroes');
}

function actorSide(root: ParentNode): CombatSide {
  return combatActorCard(root)?.getAttribute('data-gamepad-side') === 'foes' ? 'foes' : 'heroes';
}

function stepAmong(focus: HTMLElement, peers: HTMLElement[], direction: Direction): HTMLElement | null {
  const others = peers.filter((peer) => peer !== focus);
  const candidates = others.map((element, index) => ({
    id: String(index),
    rect: rectOf(element),
    containsCurrent: false,
    insideCurrent: false,
  }));
  const spatial = chooseSpatialTarget(rectOf(focus), candidates, direction);
  if (spatial != null) return others[Number(spatial)] ?? null;
  const index = peers.indexOf(focus);
  if (index < 0) return peers[0] ?? null;
  const delta = direction === 'down' || direction === 'right' ? 1 : -1;
  const next = index + delta;
  if (next < 0 || next >= peers.length) return null;
  return peers[next];
}

function focusedCard(focus: HTMLElement): HTMLElement | null {
  if (focus.matches('[data-gamepad-combat-card]')) return focus;
  const card = focus.closest('[data-gamepad-combat-card]');
  return card instanceof HTMLElement ? card : null;
}

export function combatMoveTarget(root: ParentNode, focus: HTMLElement, direction: Direction): HTMLElement | null {
  if (focus.matches('[data-gamepad-combat-action], [data-gamepad-combat-burn]')) {
    return stepAmong(focus, combatActionPeers(root), direction);
  }
  const card = focusedCard(focus);
  if (!card) return null;
  return stepAmong(card, combatCards(root), direction);
}

export function combatActivate(root: ParentNode, focus: HTMLElement | null): CombatStep {
  if (!focus) return { follow: 'home' };
  if (focus.matches('[data-gamepad-combat-burn]')) {
    return {
      click: focus,
      follow: 'burn',
      burnKey: focus.getAttribute('data-gamepad-burn-key'),
      burnRole: focus.getAttribute('data-gamepad-burn'),
    };
  }
  if (focus.matches('[data-gamepad-combat-action]')) {
    const kind = focus.getAttribute('data-gamepad-action-kind');
    if (kind === 'flee' || kind === 'cancel') return { click: focus, follow: 'home' };
    if (kind === 'confirm') return { click: focus, follow: 'idle' };
    const aim: CombatAim = focus.getAttribute('data-gamepad-target') === 'allies' ? 'allies' : 'opposite';
    return { click: focus, follow: 'target', aim };
  }
  const card = focusedCard(focus);
  if (!card) return {};
  if (isCombatAiming(root)) return { click: card, follow: 'idle' };
  if (card.getAttribute('aria-pressed') === 'true') return { follow: 'actions' };
  return { click: card, follow: 'actions' };
}

export function combatBack(root: ParentNode, focus: HTMLElement): HTMLElement | null {
  if (
    focus.matches('[data-gamepad-combat-action], [data-gamepad-combat-burn]')
    || focus.closest('[data-gamepad-combat-actions]')
  ) {
    return combatActorFocus(root);
  }
  const card = focusedCard(focus);
  if (card && isCombatAiming(root)) {
    const picked = root.querySelector<HTMLElement>('[data-gamepad-combat-action].is-picked');
    return picked ?? combatActions(root)[0] ?? null;
  }
  if (card?.getAttribute('data-gamepad-side') === 'foes') return firstCard(root, 'heroes');
  return null;
}

export function pickedCombatAction(root: ParentNode): HTMLElement | null {
  const picked = root.querySelector<HTMLElement>('[data-gamepad-combat-action].is-picked');
  return picked instanceof HTMLElement ? picked : null;
}

export function combatResolveFollow(root: ParentNode, follow: CombatFollow | undefined, aim: CombatAim = 'opposite'): HTMLElement | null {
  if (!follow || follow === 'idle' || follow === 'burn') return null;
  if (follow === 'actions') return combatActions(root)[0] ?? null;
  if (follow === 'actor') return combatActorFocus(root);
  if (follow === 'home') return combatHome(root);
  if (follow === 'target') {
    if (!isCombatAiming(root)) return null;
    const mine = actorSide(root);
    const side: CombatSide = aim === 'allies' ? mine : (mine === 'heroes' ? 'foes' : 'heroes');
    return firstCard(root, side) ?? firstCard(root, side === 'foes' ? 'heroes' : 'foes');
  }
  return null;
}

export const COMBAT_END_HINT_MS = 350;
export const COMBAT_END_HOLD_MS = 1200;

/** Kurzes Antippen bleibt folgenlos. Erst nach dem Halten wird der Kampf beendet. */
export function combatEndHoldAction(held: boolean, elapsedMs: number, canEnd: boolean): 'wait' | 'hint' | 'end' {
  if (!held || !canEnd) return 'wait';
  if (elapsedMs >= COMBAT_END_HOLD_MS) return 'end';
  if (elapsedMs >= COMBAT_END_HINT_MS) return 'hint';
  return 'wait';
}

export function combatSideEntry(root: ParentNode, side: CombatSide, focus: HTMLElement | null): HTMLElement | null {
  const card = focus ? focusedCard(focus) : null;
  if (card?.getAttribute('data-gamepad-side') === side) return null;
  return firstCard(root, side);
}
